
import React, { useEffect, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import moment from 'moment'
import { useDispatch } from 'react-redux'

import useTranslation from '@/Hooks/UseTranslation'
import { useProgrammation } from '@/Hooks/UseProgrammation'
import { getExpectedEndDate } from '@/Helpers/ProgrammationHelper'
import { useProject } from '@/Hooks/UseProject'

import { updateTaskActualDates } from '@/Services/ProgrammationManagerService'
import { updateTaskAction } from '@/Redux/Actions/ProgrammationManagerActions'

import InputText from '../../components/InputText'
import { useDatePicker } from '@/Hooks/UseDatePicker'

import SwapIcon from '@/Components/SvgImages/Swap/Swap'

import './TaskActualDates.scss'
import Spinner from '@/Components/SvgImages/Spinner/Spinner'

const TaskActualDates = () => {

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { itemOpened, patchItemOpened, setEditingItem } = useProgrammation()
  const { project, nonWorkingDays } = useProject()

  const { showDateRangePicker, showDatePicker, hideDatePicker } = useDatePicker()

  const originalStartedAt = moment(itemOpened.startedAt).format('YYYY-MM-DD')
  const originalEndedAt = itemOpened.endedAt ? moment(itemOpened.endedAt).format('YYYY-MM-DD') : ''

  const [ startedAt, setStartedAt ] = useState(originalStartedAt)
  const [ endedAt, setEndedAt ] = useState(originalEndedAt)
  const [ expectedEndDate, setExpectedEndDate ] = useState(moment(itemOpened.startedAt).format('YYYY-MM-DD'))

  const [ datesAreEqual, setDatesAreEqual ] = useState<boolean | null>(null)

  const [ swappingDates, setSwappingDates ] = useState(false)

  const saveChanges = async (startedAt: string, endedAt: string) => {

    setEditingItem(true)

    const [ error, data ] = await updateTaskActualDates(itemOpened._id, startedAt, endedAt)

    setEditingItem(false)

    if (error) {
      return false
    }

    patchItemOpened({
      startedAt: data.task.startedAt,
      endedAt: data.task.endedAt,
    })

    await dispatch(updateTaskAction(data.task, data.updatedTasks))
  }

  // if not endedAt, get estimated end date
  useEffect(() => {
  }, [itemOpened])

  useEffect(() => {

    const getEstimatedEndDate = (date: string) => {

      if (itemOpened.executeDays === 1) {

        setExpectedEndDate(date)
        return
      }

      const estimatedEndDate = getExpectedEndDate(date, itemOpened.executeDays, project.nonWorkingDays, true)
      setExpectedEndDate(estimatedEndDate)
    }

    const compareDates = (date1: string, date2: string) => {

      const date1Obj = moment(date1, 'YYYY-MM-DD')
      const date2Obj = moment(date2, 'YYYY-MM-DD')

      if (date1Obj.isSame(date2Obj, 'day')) {
        return true
      }

      return false
    }

    setStartedAt(moment(itemOpened.startedAt).format('YYYY-MM-DD'))

    if (!itemOpened.endedAt) {

      getEstimatedEndDate(itemOpened.startedAt)
      setEndedAt('')
    } else {

      setEndedAt(moment(itemOpened.endedAt).format('YYYY-MM-DD'))

      const startsAreEqual = compareDates(itemOpened.startsAt, itemOpened.startedAt)
      const endsAreEqual = compareDates(itemOpened.endsAt, itemOpened.endedAt)

      setDatesAreEqual(startsAreEqual && endsAreEqual)
    }
  }, [itemOpened])

  const handleChangeDates = async (dates: any) => {

    const {
      startDate,
      endDate
    } = dates

    setStartedAt(startDate)
    setEndedAt(endDate)

    await saveChanges(startDate, endDate)

    hideDatePicker()
  }

  const handleStartedAtOpen = () => {

    if (endedAt) {

      handleShowDateRangePicker()
    } else {

      showDatePicker({
        date: startedAt,
        projectStartDate: project.startsAt,
        projectEndDate: project.endsAt,
        nonWorkingDays,
        onChange: (startDate) => {

          handleChangeDates({ startDate})
        }
      })
    }
  }

  const handleShowDateRangePicker = () => {

    if (itemOpened.hasSubTasks) return

    showDateRangePicker({
      startDate: startedAt,
      endDate: endedAt,
      projectStartDate: project.startsAt,
      projectEndDate: project.endsAt,
      nonWorkingDays,
      onChangeRange: handleChangeDates
    })
  }

  const handleSwapDates = async (e: any) => {
    e.preventDefault()
    e.stopPropagation()

    setSwappingDates(true)

    await handleChangeDates({
      startDate: moment(itemOpened.startsAt).format('YYYY-MM-DD'),
      endDate: moment(itemOpened.endsAt).format('YYYY-MM-DD'),
    })

    setSwappingDates(false)
  }

  return (
    <>
      <Row>
        <Col>
          <h5 className='group-title'>
            {t('PROGRAMMATION_MANAGER:ActualDatesTitleLabel')}
          </h5>
        </Col>
      </Row>
      <Row>
        <Col sm={4}>
          <Form.Group>
            <Form.Label>
              {t('PROGRAMMATION_MANAGER:StartedAtLabel')}
            </Form.Label>
            <InputText
              value={moment(startedAt).format('DD/MM/YYYY')}
              placeholder="dd/mm/yyyy"
              onClick={handleStartedAtOpen}
              asButton
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group>
            <Form.Label>
              {!itemOpened.endedAt && t('PROGRAMMATION_MANAGER:EstimatedEndDateLabel')}
              {itemOpened.endedAt &&t('PROGRAMMATION_MANAGER:EndedAtLabel')}
            </Form.Label>
            {itemOpened.endedAt && (
              <InputText
                value={moment(endedAt).format('DD/MM/YYYY')}
                placeholder="dd/mm/yyyy"
                onClick={handleShowDateRangePicker}
                asButton
              />
            )}
            {!itemOpened.endedAt && (
              <InputText
                value={moment(expectedEndDate).format('DD/MM/YYYY')}
                placeholder="dd/mm/yyyy"
                asButton
              />
            )}
          </Form.Group>
        </Col>
      </Row>
      {!datesAreEqual && (
        <>
          <Row>
            <Col>
              {t('PROGRAMMATION_MANAGER:ActualDatesDoNotMatchScheduledDates')}
            </Col>
          </Row>
          <Row>
            <Col>
              <button className="btn btn-normal" onClick={handleSwapDates}>
                {!swappingDates && (
                  <SwapIcon height={16} width={16} />
                )}
                {swappingDates && (
                  <Spinner
                    height={16}
                    width={16}
                  />
                )}{' '}
                {t('PROGRAMMATION_MANAGER:AlignDatesButton')}
              </button>
            </Col>
          </Row>
        </>
      )}
    </>
  )
}

export default TaskActualDates
